import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
} from "@angular/common/http";
import {
    CUSTOM_ELEMENTS_SCHEMA,
    NgModule,
    NO_ERRORS_SCHEMA,
} from "@angular/core";
import { MatMomentDateModule } from "@angular/material-moment-adapter";
import { MatIconModule } from "@angular/material/icon";
import { BrowserModule, Title } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { RouterModule, Routes } from "@angular/router";
import { FuseProgressBarModule } from "@fuse/components";
import { FuseModule } from "@fuse/fuse.module";
import { FuseSharedModule } from "@fuse/shared.module";
import { TranslateModule } from "@ngx-translate/core";
import { SimpleNotificationsModule } from "angular2-notifications";
import { AppComponent } from "app/app.component";
import { fuseConfig } from "app/fuse-config";
import { environment } from "environments/environment";
import { SocketIoConfig, SocketIoModule } from "ngx-socket-io";
import { ErrorMessageTranslatorService } from "./ErrorMessageTranslatorService";
import { AuthGuard } from "./guard/auth.guard";
import { TokenInterceptor } from "./interceptors/token.interceptor";
import { CageModule } from "./layout/cage/cage.module";
import { ChatModule } from "./main/chat/chat.module";
import { ConfirmRegistrationModule } from "./main/confirm-registration/confirm-registration.module";
import { CookieModule } from "./main/cookie/cookie.module";
import { DialogConfirm } from "./main/dialog-confirm/dialog-confirm.component";
import { ForgotPasswordModule } from "./main/forgot-password/forgot-password.module";
import { DialogForgotPassword } from "./main/home/dialog-component/forgot-password/dialog-forgot-password.component";
import { DialogRegistrationComponent } from "./main/home/dialog-component/registration/registration.component";
import { HomeModule } from "./main/home/home.module";
import { LoginModule } from "./main/login/login.module";
import { PrivacyModule } from "./main/privacy/privacy.module";
import { VideochatModule } from "./main/videochat/videochat.module";
import { WaitingModule } from "./main/waiting/waiting.module";
import { GoogleAnalyticsService } from "./services/analytics/google-analitics-service";
import { ApiCalendarService } from "./services/api/api-calendar-service";
import { ApiForgotPasswordService } from "./services/api/api-forgot-password.service";
import { ApiItalyGeoService } from "./services/api/api-italy-geo.service";
import { ApiLoginService } from "./services/api/api-login.service";
import { ApiQueueService } from "./services/api/api-queue.service";
import { ApiTicketHistoryService } from "./services/api/api-ticket-history.service";
import { ApiTicketServiceService } from "./services/api/api-ticket-service-service";
import { ApiTicketService } from "./services/api/api-ticket.service";
import { ApiUserService } from "./services/api/api-user.service";
import { ChatService } from "./services/api/chat-messages.service";
import { AuthService } from "./services/auth/auth.service";
import { LocalStorageService } from "./services/local-storage/local-storage.service";
import { ThemeService } from "./services/theme/theme.service";
import { SocketService } from "./services/socket/socket.service";
import { ConditionModule } from "./main/condition/condition.module";
import { NgcCookieConsentConfig } from "ngx-cookieconsent";
import { provideNgcCookieConsent } from "ngx-cookieconsent";
import { NgcCookieConsentModule } from "ngx-cookieconsent";
import { DialogFeedbakComponent } from "./main/dialog-feedback/dialog-feedback.component";
import { UserFeedbackSender } from "./services/api/api-user-feedback.service";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { DialogNewsComponent } from "./main/dialog-news/dialog-news.component";
import { ApiArticleService } from "./services/api/api-article-service";
import { IncomingCallModule } from "./main/incoming-call/incoming-call.module";
import { ApiPlatformService } from "./services/api/api-platform-service.service";
import { ApiTicketCategoryService } from "./services/api/api-ticket-category-service.service";
import { ApiTenantService } from "./services/api/api-tenant.service";

const appRoutes: Routes = [
    {
        path: "**",
        redirectTo: "login",
    },
];

const options = {
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: Infinity,
    multiplex: false,
    path: environment.ws_path,
    transports: ["websocket"],
};

const wssPort = environment.ws_port ? ":" + environment.ws_port : "";
const config: SocketIoConfig = {
    url: environment.ws_url + wssPort,
    options: options,
};
const cookieConfig: NgcCookieConsentConfig = {
    cookie: {
        domain: environment.domain,
    },
    position: "bottom",
    theme: "classic",
    palette: {
        popup: {
            background: "#000000",
            text: "#ffffff",
        },
        button: {
            background: "#f1d600",
            text: "#000000",
        },
    },
    type: "info",
    content: {
        message:
            "Questa applicazione utilizza i cookie. Per maggiori dettagli clicca ",
        dismiss: "Accetta",
        deny: "Refiuta cookies",
        link: "qui.",
        href: "/cookie",
        policy: "Cookie Policy",
    },
};

@NgModule({
    declarations: [
        AppComponent,
        DialogConfirm,
        DialogForgotPassword,
        DialogRegistrationComponent,
        DialogFeedbakComponent,
        DialogNewsComponent,
    ],
    bootstrap: [AppComponent],
    schemas: [NO_ERRORS_SCHEMA, CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        ConditionModule,
        FuseSharedModule,
        BrowserModule,
        BrowserAnimationsModule,
        RouterModule.forRoot(appRoutes),
        TranslateModule.forRoot(),
        // Material moment date module
        MatMomentDateModule,
        // Fuse modules
        FuseModule.forRoot(fuseConfig),
        FuseProgressBarModule,
        // App modules
        CageModule,
        HomeModule,
        LoginModule,
        WaitingModule,
        ChatModule,
        VideochatModule,
        CookieModule,
        ForgotPasswordModule,
        ConfirmRegistrationModule,
        SimpleNotificationsModule.forRoot(),
        SocketIoModule.forRoot(config),
        MatIconModule,
        PrivacyModule,
        NgcCookieConsentModule.forRoot(cookieConfig),
        IncomingCallModule,
    ],
    providers: [
        SocketService,
        ApiQueueService,
        ApiCalendarService,
        ApiTicketServiceService,
        AuthService,
        ApiForgotPasswordService,
        LocalStorageService,
        ThemeService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true,
        },
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                subscriptSizing: "dynamic",
            },
        },
        ApiLoginService,
        ApiUserService,
        ApiItalyGeoService,
        ApiTicketService,
        ApiTicketHistoryService,
        ChatService,
        AuthGuard,
        GoogleAnalyticsService,
        Title,
        ErrorMessageTranslatorService,
        UserFeedbackSender,
        ApiArticleService,
        ApiPlatformService,
        ApiTicketCategoryService,
        ApiTenantService,
        provideNgcCookieConsent(cookieConfig),
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class AppModule {}
